import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Calendar,
  Clock,
  MapPin,
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Flame,
  CheckCircle2
} from 'lucide-react';
import WhatsAppIcon from '../../components/WhatsAppIcon';
import GoogleMapsIcon from '../../components/GoogleMapsIcon';
import '../../styles/HomamPortal.css';
import {
  MOCK_SITE_SETTINGS,
  MOCK_NAKSHATRAS,
  getWhatsAppLink,
  getNakshatraImage
} from '../../data/homamData';
import HomamSubNavbar from '../../components/HomamSubNavbar';
import HomamEnquiryForm from '../../components/HomamEnquiryForm';
import HomamFooter from '../../components/HomamFooter';
import HomamSanctumDarshan from '../../components/HomamSanctumDarshan';
import { getHomamPath } from '../../utils/domain';

export function HomamDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const nakshatra =
    MOCK_NAKSHATRAS.find((n) => n.slug === slug) || MOCK_NAKSHATRAS[0];

  const currentIndex = MOCK_NAKSHATRAS.findIndex((n) => n.id === nakshatra.id);
  const prevNakshatra =
    currentIndex > 0
      ? MOCK_NAKSHATRAS[currentIndex - 1]
      : MOCK_NAKSHATRAS[MOCK_NAKSHATRAS.length - 1];
  const nextNakshatra =
    currentIndex < MOCK_NAKSHATRAS.length - 1
      ? MOCK_NAKSHATRAS[currentIndex + 1]
      : MOCK_NAKSHATRAS[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.title = `${nakshatra.nameEn} Nakshatra Homam (Day ${nakshatra.orderIndex}) | Sri Gnana Sai Baba Mandir`;
  }, [slug, nakshatra]);

  const whatsappHref = getWhatsAppLink({
    nakshatraName: nakshatra.nameEn,
    nakshatraDay: nakshatra.orderIndex,
    source: `Dedicated Nakshatra Page: ${nakshatra.nameEn}`
  });

  return (
    <div className="homam-portal-wrapper">
      <HomamSubNavbar />

      {/* Hero & Breadcrumb */}
      <section className="homam-hero-sec" style={{ textAlign: 'left', padding: '40px 0 60px 0' }}>
        <div className="homam-container">
          {/* Breadcrumb Navigation & Mobile Back Action */}
          <div className="homam-breadcrumb-row">
            <button
              type="button"
              className="homam-mobile-page-back-btn"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate(`${getHomamPath('/')}#nakshatras`);
                }
              }}
              aria-label="Back to 27 Nakshatras"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back</span>
            </button>

            <div className="homam-breadcrumb-links">
              <Link to={getHomamPath('/')} className="homam-breadcrumb-link">Home</Link>
              <span className="homam-breadcrumb-sep">/</span>
              <Link to={`${getHomamPath('/')}#nakshatras`} className="homam-breadcrumb-link">27 Nakshatras</Link>
              <span className="homam-breadcrumb-sep">/</span>
              <strong className="homam-breadcrumb-current">{nakshatra.nameEn}</strong>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'clamp(20px, 3vw, 36px)', alignItems: 'center' }}>
            <div style={{ textAlign: 'left' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
                <div className="homam-trending-day-badge">
                  <div className="homam-trending-cal-icon-chip">
                    <Flame className="w-3.5 h-3.5" />
                  </div>
                  <span className="homam-trending-day-text">
                    <span>DAY {nakshatra.orderIndex}</span>
                    <span className="homam-trending-day-dot"></span>
                    <span>27 MAHA HOMAM</span>
                  </span>
                </div>

                <div className="homam-trending-date-pill">
                  <div className="homam-trending-date-icon-box">
                    <Calendar className="w-3.5 h-3.5" />
                  </div>
                  <span className="homam-trending-date-label">
                    {nakshatra.assignedDate || 'Date to be announced'}
                  </span>
                </div>
              </div>

              <p className="homam-hero-tamil" style={{ margin: '0 0 4px 0' }}>
                {nakshatra.nameTa} நட்சத்திர மகா ஹோமம்
              </p>
              <h1 style={{ fontSize: '2.5rem', fontWeight: 900, color: '#0f2d5c', margin: '0 0 14px 0', lineHeight: 1.2 }}>
                {nakshatra.nameEn} Nakshatra Homam
              </h1>
              <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: '0 0 22px 0' }}>
                {nakshatra.introContent}
              </p>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-card-register"
                  style={{ padding: '12px 20px', fontSize: 13 }}
                >
                  <WhatsAppIcon size={16} style={{ marginRight: 6 }} />
                  <span>Register on WhatsApp for Day {nakshatra.orderIndex}</span>
                </a>

                <a
                  href="#register-section"
                  className="btn-card-view-details"
                  style={{ padding: '12px 20px', fontSize: 13 }}
                >
                  <span>Online Homam Form</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Sacred Nakshatra Deity & Star Banner */}
            <div style={{ borderRadius: 24, overflow: 'hidden', border: '2px solid #fde68a', boxShadow: '0 8px 30px rgba(184,134,11,0.15)', background: '#0a192f' }}>
              <div style={{ background: 'radial-gradient(circle at 50% 50%, #ffffff 0%, #FAF6EE 65%, #F5EDE0 100%)', height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
                <img
                  src={getNakshatraImage(nakshatra)}
                  alt={`${nakshatra.nameEn} - ${nakshatra.deity}`}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = `/images/nakshatras/${nakshatra.slug || 'krittika'}.jpg`;
                  }}
                />
              </div>
              <div style={{ padding: 16, color: '#ffffff', textAlign: 'left' }}>
                <strong style={{ color: '#f5c542', fontSize: 13, display: 'block' }}>{nakshatra.nameEn} ({nakshatra.nameTa}) Sacred Deity</strong>
                <span style={{ fontSize: 11.5, color: '#e2e8f0' }}>{nakshatra.deity} — Day {nakshatra.orderIndex} of 27 Maha Homam</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Details Grid */}
      <section className="homam-grid-wrap" style={{ borderTop: '1px solid #EADDC7' }}>
        <div className="homam-container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'clamp(18px, 2.5vw, 30px)' }}>
            {/* Left Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, textAlign: 'left' }}>
              {/* Stellar Attributes Box */}
              <div style={{ background: '#ffffff', borderRadius: 24, border: '1px solid #EADDC7', padding: 26, boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 18px 0' }}>
                  <Sparkles className="w-5 h-5" style={{ color: '#b8860b' }} />
                  <span>Sacred Stellar Attributes</span>
                </h2>

                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 12 }}>
                  <div style={{ flex: '1 1 160px', maxWidth: 220, minWidth: 150, background: '#FAF7F2', padding: 14, borderRadius: 14, border: '1px solid #EADDC7', boxSizing: 'border-box' }}>
                    <span style={{ fontSize: 11, color: '#64748b', display: 'block' }}>Presiding Deity:</span>
                    <strong style={{ fontSize: 13, color: '#0f2d5c', display: 'block' }}>{nakshatra.deity}</strong>
                    <span style={{ fontSize: 11, color: '#946608', fontWeight: 700 }}>{nakshatra.deityTa}</span>
                  </div>
                  <div style={{ flex: '1 1 160px', maxWidth: 220, minWidth: 150, background: '#FAF7F2', padding: 14, borderRadius: 14, border: '1px solid #EADDC7', boxSizing: 'border-box' }}>
                    <span style={{ fontSize: 11, color: '#64748b', display: 'block' }}>Ruling Planet:</span>
                    <strong style={{ fontSize: 13, color: '#0f2d5c', display: 'block' }}>{nakshatra.rulingPlanet}</strong>
                    <span style={{ fontSize: 11, color: '#946608', fontWeight: 700 }}>{nakshatra.rulingPlanetTa}</span>
                  </div>
                  <div style={{ flex: '1 1 160px', maxWidth: 220, minWidth: 150, background: '#FAF7F2', padding: 14, borderRadius: 14, border: '1px solid #EADDC7', boxSizing: 'border-box' }}>
                    <span style={{ fontSize: 11, color: '#64748b', display: 'block' }}>Star Symbol:</span>
                    <strong style={{ fontSize: 13, color: '#0f2d5c', display: 'block' }}>{nakshatra.symbol}</strong>
                    <span style={{ fontSize: 11, color: '#946608', fontWeight: 700 }}>{nakshatra.symbolTa}</span>
                  </div>
                  <div style={{ flex: '1 1 160px', maxWidth: 220, minWidth: 150, background: '#FAF7F2', padding: 14, borderRadius: 14, border: '1px solid #EADDC7', boxSizing: 'border-box' }}>
                    <span style={{ fontSize: 11, color: '#64748b', display: 'block' }}>Zodiac (Rasi):</span>
                    <strong style={{ fontSize: 13, color: '#0f2d5c', display: 'block' }}>{nakshatra.rasi}</strong>
                    <span style={{ fontSize: 11, color: '#946608', fontWeight: 700 }}>{nakshatra.rasiTa}</span>
                  </div>
                  <div style={{ flex: '1 1 160px', maxWidth: 220, minWidth: 150, background: '#FAF7F2', padding: 14, borderRadius: 14, border: '1px solid #EADDC7', boxSizing: 'border-box' }}>
                    <span style={{ fontSize: 11, color: '#64748b', display: 'block' }}>Sacred Tree (Vruksham):</span>
                    <strong style={{ fontSize: 13, color: '#0f2d5c', display: 'block' }}>{nakshatra.sacredTree}</strong>
                  </div>
                  <div style={{ flex: '1 1 160px', maxWidth: 220, minWidth: 150, background: '#FAF7F2', padding: 14, borderRadius: 14, border: '1px solid #EADDC7', boxSizing: 'border-box' }}>
                    <span style={{ fontSize: 11, color: '#64748b', display: 'block' }}>Cosmic Element:</span>
                    <strong style={{ fontSize: 13, color: '#0f2d5c', display: 'block' }}>{nakshatra.sacredElement}</strong>
                  </div>
                </div>
              </div>

              {/* Sacred Moola Mantra */}
              <div style={{ background: '#fef3c7', borderRadius: 20, border: '2px solid #fde68a', padding: 24 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <Flame className="w-4 h-4" style={{ color: '#b8860b' }} />
                  <span>Sacred Moola Mantra for Day {nakshatra.orderIndex}</span>
                </span>
                <p style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: '#0f2d5c', margin: '0 0 6px 0', fontStyle: 'italic' }}>
                  &ldquo;{nakshatra.moolaMantra}&rdquo;
                </p>
                <p style={{ fontSize: 12, color: '#475569', margin: 0 }}>
                  Chanted 10,008 times by Vedic Scholars during the sacred Ahuti fire ritual on this day.
                </p>
              </div>

              {/* Benefits */}
              <div style={{ background: '#ffffff', borderRadius: 24, border: '1px solid #EADDC7', padding: 26, boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px 0' }}>
                  <ShieldCheck className="w-5 h-5" style={{ color: '#b8860b' }} />
                  <span>Spiritual Merits & Homam Benefits</span>
                </h2>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                  {nakshatra.benefits.map((b, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12, borderRadius: 12, background: '#FAF7F2', border: '1px solid #EADDC7' }}>
                      <CheckCircle2 className="w-4 h-4" style={{ color: '#b8860b', flexShrink: 0, marginTop: 2 }} />
                      <span style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.4 }}>{b}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Specific Form */}
              <div id="register-section">
                <HomamEnquiryForm
                  initialNakshatraId={nakshatra.id}
                  initialPreferredDay={`Day ${nakshatra.orderIndex} - ${nakshatra.nameEn}`}
                  source={`Nakshatra Detail Page: ${nakshatra.nameEn}`}
                />
              </div>
            </div>

            {/* Right Sidebar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, textAlign: 'left' }}>
              {/* Timing */}
              <div style={{ background: '#ffffff', borderRadius: 24, border: '1px solid #EADDC7', padding: 24, boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 14px 0' }}>
                  <Clock className="w-5 h-5" style={{ color: '#b8860b' }} />
                  <span>Day {nakshatra.orderIndex} Timetable</span>
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 5 }}>
                    <span style={{ color: '#64748b' }}>Temple Open:</span>
                    <strong style={{ color: '#0f2d5c' }}>05:50 AM</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 5 }}>
                    <span style={{ color: '#64748b' }}>Kakad Aarti:</span>
                    <strong style={{ color: '#0f2d5c' }}>06:15 AM</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 5 }}>
                    <span style={{ color: '#64748b' }}>Gho Pooja:</span>
                    <strong style={{ color: '#0f2d5c' }}>07:00 AM</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 5 }}>
                    <span style={{ color: '#64748b' }}>Baba Abhishekam:</span>
                    <strong style={{ color: '#0f2d5c' }}>07:30 AM</strong>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, borderBottom: '1px solid #d4af37', padding: '6px 8px', background: '#fef3c7', borderRadius: 8, margin: '2px 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#92400e', fontWeight: 700 }}>Sahasranama Homam:</span>
                      <strong style={{ color: '#92400e', fontWeight: 800 }}>09:30 AM – 11:00 AM</strong>
                    </div>
                    <span style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>
                      * Devotees requested to arrive by 09:00 AM
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 5 }}>
                    <span style={{ color: '#64748b' }}>Afternoon Aarti:</span>
                    <strong style={{ color: '#0f2d5c' }}>12:00 PM</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 5 }}>
                    <span style={{ color: '#64748b' }}>Annadhanam:</span>
                    <strong style={{ color: '#0f2d5c' }}>12:30 PM</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 5 }}>
                    <span style={{ color: '#64748b' }}>Temple Closed:</span>
                    <strong style={{ color: '#0f2d5c' }}>02:00 PM</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 5 }}>
                    <span style={{ color: '#64748b' }}>Temple Re-opens:</span>
                    <strong style={{ color: '#0f2d5c' }}>04:30 PM</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 5 }}>
                    <span style={{ color: '#64748b' }}>Dhoop Aarti:</span>
                    <strong style={{ color: '#0f2d5c' }}>06:00 PM</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #EADDC7', paddingBottom: 5 }}>
                    <span style={{ color: '#64748b' }}>Shej Aarti:</span>
                    <strong style={{ color: '#0f2d5c' }}>08:30 PM</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>Temple Closed:</span>
                    <strong style={{ color: '#0f2d5c' }}>09:00 PM</strong>
                  </div>
                </div>
              </div>

              {/* Venue */}
              <div style={{ background: '#ffffff', borderRadius: 24, border: '1px solid #EADDC7', padding: 24, boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 10px 0' }}>
                  <MapPin className="w-5 h-5" style={{ color: '#b8860b' }} />
                  <span>Sanctum Venue</span>
                </h3>
                <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.5, margin: '0 0 10px 0' }}>
                  <strong style={{ display: 'block', color: '#0f2d5c' }}>{MOCK_SITE_SETTINGS.mandirName}</strong>
                  {MOCK_SITE_SETTINGS.venueAddress}, <br />
                  {MOCK_SITE_SETTINGS.venueCity}
                </p>
                <a
                  href={MOCK_SITE_SETTINGS.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#b8860b',
                    textDecoration: 'none',
                    padding: '4px 10px',
                    borderRadius: 6,
                    background: '#fef3c7',
                    border: '1px solid #fde68a',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <GoogleMapsIcon size={16} />
                  <span>View on Google Maps</span>
                </a>
              </div>

              {/* WhatsApp Help */}
              <div style={{ background: '#ffffff', borderRadius: 24, border: '2px solid #fde68a', padding: 24, textAlign: 'center' }}>
                <div style={{ width: 58, height: 58, borderRadius: 18, background: '#fef3c7', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#25D366', margin: '0 auto 14px auto', boxShadow: '0 4px 12px rgba(37, 211, 102, 0.15)' }}>
                  <WhatsAppIcon size={36} />
                </div>
                <h4 style={{ fontSize: 14, fontWeight: 800, color: '#0f2d5c', margin: '0 0 6px 0' }}>Need Help with {nakshatra.nameEn} Homam?</h4>
                <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.4, margin: '0 0 14px 0' }}>
                  Chat directly with Sri Gnana Sai Baba Mandir seva coordinators for Gothram verification and travel guidance.
                </p>
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-card-register"
                  style={{ width: '100%', padding: '10px', textAlign: 'center' }}
                >
                  <WhatsAppIcon size={16} style={{ marginRight: 6 }} />
                  <span>Chat on WhatsApp</span>
                </a>
              </div>

              {/* Sacred Sanctum Darshan Collage — fills empty space beside registration form */}
              <div className="homam-darshan-detail-col">
                <HomamSanctumDarshan />
              </div>
            </div>
          </div>

          {/* Prev / Next Star Navigation Footer */}
          <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid #EADDC7', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            <Link
              to={getHomamPath(`/nakshatra/${prevNakshatra.slug}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, background: '#ffffff', borderRadius: 16, border: '1px solid #EADDC7', textDecoration: 'none', textAlign: 'left' }}
            >
              <div style={{ padding: 8, borderRadius: 10, background: '#FAF7F2' }}>
                <ArrowLeft className="w-5 h-5" style={{ color: '#b8860b' }} />
              </div>
              <div>
                <span style={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', display: 'block', fontWeight: 700 }}>
                  Previous Star (Day {prevNakshatra.orderIndex})
                </span>
                <strong style={{ fontSize: 13.5, color: '#0f2d5c' }}>
                  {prevNakshatra.nameEn} ({prevNakshatra.nameTa})
                </strong>
              </div>
            </Link>

            <Link
              to={getHomamPath(`/nakshatra/${nextNakshatra.slug}`)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, padding: 14, background: '#ffffff', borderRadius: 16, border: '1px solid #EADDC7', textDecoration: 'none', textAlign: 'right' }}
            >
              <div>
                <span style={{ fontSize: 10.5, color: '#64748b', textTransform: 'uppercase', display: 'block', fontWeight: 700 }}>
                  Next Star (Day {nextNakshatra.orderIndex})
                </span>
                <strong style={{ fontSize: 13.5, color: '#0f2d5c' }}>
                  {nextNakshatra.nameEn} ({nextNakshatra.nameTa})
                </strong>
              </div>
              <div style={{ padding: 8, borderRadius: 10, background: '#FAF7F2' }}>
                <ArrowRight className="w-5 h-5" style={{ color: '#b8860b' }} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      <HomamFooter />
    </div>
  );
}

export default HomamDetail;
